/**
 * Authority Continuity Intelligence (ACI) — Phase 0–6.4 constants.
 * Super-admin linkage / authority-continuity tooling (internal investigations).
 * Mirror: backend/src/config/authorityContinuityIntelligence.ts
 */

/** User-visible product name (super-admin UI, API messages). */
export const AUTHORITY_CONTINUITY_INTELLIGENCE_DISPLAY_NAME =
  'Authority Continuity Intelligence' as const;

/** Short label (nav, badges). */
export const AUTHORITY_CONTINUITY_INTELLIGENCE_SHORT_LABEL = 'ACI' as const;

/**
 * Firestore `system/featureFlags` document key.
 * Must match `featureFlagService` defaultFlags and backend config.
 */
export const ACI_LINKAGE_FLAG_KEY = 'authorityContinuityIntelligence' as const;

/**
 * Client env kill switch (Phase 2+ UI). When `false`, ACI surfaces stay hidden regardless of Firestore flag.
 */
export function isAciLinkageEnvDisabled(): boolean {
  return process.env.REACT_APP_ACI_LINKAGE_ENABLED === 'false';
}

/** Super-admin UI route (must match `App.tsx` `AdminRoute` path). */
export const ACI_ADMIN_UI_ROUTE_PATH = '/admin/authority-continuity-intelligence' as const;

/**
 * Human-readable description of this workspace (shown in the SPA legal/status card).
 * Keep in sync with `message` on `GET /api/admin/aci/phase0-status` in the backend when that copy changes.
 * The UI uses this string as the primary instruction so operators are not blocked on redeploying the API.
 */
export const ACI_SUPERADMIN_WORKSPACE_INTRO =
  'This workspace is for authorized Tranzit super-admins. Use it to review motor-carrier snapshots, run linkage previews, save analyst feedback, record internal timeline notes, manage investigation cases and formal actions, log regulatory-share intent when your environment allows it, and build confidential export packets. Activity is audited and responses are marked so browsers should not cache this page. Open "Scoring limits and risk bands" below for the human-readable limits, or use the operator guide link under the page title for a full plain-language PDF.' as const;

/**
 * ACI admin API paths for use with {@link getApiUrl} from `utils/backendUrl.ts`.
 * `getApiUrl()` already includes the `/api` prefix (e.g. `http://localhost:3001/api` or `/api` in dev HTTPS proxy mode).
 * Do not prefix these with `/api` again — that would produce `/api/api/...` and fail locally.
 *
 * Full path from host root is always `/api/admin/aci/...` (mirror `backend/src/config/authorityContinuityIntelligence.ts`).
 */
export const ACI_ADMIN_API = {
  PHASE0_STATUS_PATH: '/admin/aci/phase0-status',
  REGULATORY_SNAPSHOT_PATH: '/admin/aci/regulatory-snapshot',
  LINKAGE_PREVIEW_PATH: '/admin/aci/linkage-preview',
  LINKAGE_ANALYST_FEEDBACK_PATH: '/admin/aci/linkage-analyst-feedback',
  LINKAGE_PREVIEW_RUNS_PATH: '/admin/aci/linkage-preview-runs',
  BEHAVIOR_EVENT_PATH: '/admin/aci/behavior-event',
  BEHAVIOR_EVENTS_PATH: '/admin/aci/behavior-events',
  /** Phase 6.1: cases CRUD + evidence (path base; append `/${caseId}` or `/…/evidence`). */
  CASES_PATH: '/admin/aci/cases',
  /** Phase 6.3: regulatory share intent log (POST append + GET list). */
  REGULATORY_SHARE_INTENTS_PATH: '/admin/aci/regulatory-share-intents',
  /** Phase 6.4: §1.7 export packet manifest (GET). */
  EXPORT_PACKET_MANIFEST_PATH: '/admin/aci/export-packet-manifest',
  /** Phase 6.4: assemble minimized export JSON (POST). */
  EXPORT_PACKET_ASSEMBLE_PATH: '/admin/aci/export-packet-assemble',
} as const;

/** Phase 4 v1 linkage preview JSON contract (mirror backend). */
export const ACI_PHASE_4_LINKAGE_PREVIEW_SCHEMA_VERSION = '2026-05-03.1' as const;

/** Phase 4.5 — frozen v1 hard-match scorer (mirror backend). */
export const ACI_LINKAGE_WEIGHTS_VERSION_V1 = '2026-05-03.1' as const;

/** Phase 5.2 — v2 scorer version string (mirror backend). */
export const ACI_LINKAGE_WEIGHTS_VERSION_V2 = '2026-05-30.1' as const;

/** Default / legacy: same as v1 (mirror backend `ACI_LINKAGE_WEIGHTS_VERSION`). */
export const ACI_LINKAGE_WEIGHTS_VERSION = ACI_LINKAGE_WEIGHTS_VERSION_V1;

/** POST linkage-preview `scoringModel` (mirror backend). */
export const ACI_LINKAGE_SCORING_MODEL = {
  V1: 'v1',
  V2: 'v2',
} as const;

export type AciLinkageScoringModel = (typeof ACI_LINKAGE_SCORING_MODEL)[keyof typeof ACI_LINKAGE_SCORING_MODEL];

/** Max behavior docs read for Phase 5.2 linkage adjunct (mirror backend). */
export const ACI_PHASE5_2_LINKAGE_BEHAVIOR_QUERY_LIMIT = 100 as const;

export const ACI_PHASE5_2_BEHAVIOR_WINDOW_DAYS = 90 as const;

/** Phase 5.3 shadow JSON schema version (mirror backend). */
export const ACI_PHASE5_3_SHADOW_SCHEMA_VERSION = '2026-05-30.1' as const;

/** Phase 4.3 — threshold table version label (mirror backend `ACI_PHASE4_THRESHOLD_TABLE_VERSION`). */
export const ACI_PHASE4_THRESHOLD_TABLE_VERSION = '2026-05-03.1' as const;

/**
 * Phase 4.3 — conservative v1 threshold / cap table (mirror backend `buildPhase4ThresholdTableV1` semantics).
 */
export function buildPhase4ThresholdTableV1(): Record<string, unknown> {
  return {
    tableVersion: ACI_PHASE4_THRESHOLD_TABLE_VERSION,
    linkageWeightsVersion: ACI_LINKAGE_WEIGHTS_VERSION_V1,
    confluenceMinDistinctFamilies: ACI_CONFLUENCE_MIN_DISTINCT_FAMILIES,
    suggestedInternalBandDefinitions: {
      '0': 'No distinct hard-signal families in the overlap list.',
      '1': 'Single-family overlap only; treat as weak signal (high false-positive risk if used alone).',
      '2': 'Two distinct families among {email, phone, mc, dot, vin}.',
      '3plus':
        'Three or more distinct families; Phase 4.1 confluence may apply when count >= confluenceMinDistinctFamilies.',
    },
    queryCaps: {
      maxAnchorUsers: ACI_LINKAGE_PREVIEW_MAX_ANCHOR_USERS,
      maxEmailQueries: ACI_LINKAGE_PREVIEW_MAX_EMAIL_QUERIES,
      maxPhoneQueryAttempts: ACI_LINKAGE_PREVIEW_MAX_PHONE_QUERIES,
      maxVinQueries: ACI_LINKAGE_PREVIEW_MAX_VIN_QUERIES,
    },
    phase41RuleSummary: `phase41Confluence is true when distinct hard-signal family count >= ${ACI_CONFLUENCE_MIN_DISTINCT_FAMILIES} among {email, phone, mc, dot, vin}.`,
  };
}

/** Persisted analyst feedback document schema version (mirror backend). */
export const ACI_LINKAGE_ANALYST_FEEDBACK_SCHEMA_VERSION = '2026-05-03.1' as const;

/** Persisted linkage preview run document schema (mirror backend). */
export const ACI_LINKAGE_PREVIEW_RUN_SCHEMA_VERSION = '2026-05-30.2' as const;

/** Phase 5.1 — Firestore `aciInternalBehaviorEvents` document schema (mirror backend). */
export const ACI_PHASE_5_BEHAVIOR_EVENT_SCHEMA_VERSION = '2026-05-01.1' as const;

/** Phase 6.1 — Firestore `aciInternalCases` document schema (mirror backend). */
export const ACI_PHASE_6_1_CASE_SCHEMA_VERSION = '2026-04-30.1' as const;

export const ACI_CASE_STATUS_V1 = {
  OPEN: 'open',
  IN_REVIEW: 'in_review',
  CLOSED: 'closed',
} as const;

export type AciCaseStatusV1 = (typeof ACI_CASE_STATUS_V1)[keyof typeof ACI_CASE_STATUS_V1];

export const ACI_CASE_EVIDENCE_KIND_V1 = {
  LINKAGE_PREVIEW_RUN: 'linkage_preview_run',
} as const;

/** Phase 6.2 — Firestore `aciInternalCaseActions` document schema (mirror backend). */
export const ACI_PHASE_6_2_CASE_ACTION_SCHEMA_VERSION = '2026-04-30.1' as const;

/** Phase 6.3 — Firestore `aciInternalRegulatoryShareIntents` document schema (mirror backend). */
export const ACI_PHASE_6_3_REGULATORY_SHARE_INTENT_SCHEMA_VERSION = '2026-04-30.1' as const;

/** Phase 6.4 — export packet JSON schema (mirror backend). */
export const ACI_EXPORT_PACKET_SCHEMA_VERSION = '2026-04-30.1' as const;

/** Watermark bundle on assembled packets (mirror backend). */
export const ACI_EXPORT_WATERMARK_V1 = {
  classification: 'INTERNAL_CONFIDENTIAL_ACI_V1',
  distributionRestriction:
    'Super-admin internal use only. Do not distribute to brokers, carriers, regulators, or public channels without counsel-approved process. Aggregated / posture facts per roadmap §1.7 and § v1 product directive.',
} as const;

export const ACI_REGULATORY_SHARE_COUNSEL_REF_MIN_LEN = 8 as const;
export const ACI_REGULATORY_SHARE_COUNSEL_REF_MAX_LEN = 200 as const;

/** Max UTF-8 length for optional regulatory share intent notes (mirror backend). */
export const ACI_REGULATORY_SHARE_INTENT_NOTES_MAX_LENGTH = 800 as const;

/** Max UTF-8 length for optional case action notes (mirror backend `getAciCaseActionNotesMaxLength`). */
export const ACI_CASE_ACTION_NOTES_MAX_LENGTH = 1500 as const;

export const ACI_CASE_ACTION_SOURCE_V1 = {
  ADMIN_API: 'admin_api',
} as const;

export const ACI_ENFORCEMENT_ACTION_TYPE_V1 = {
  HOLD_ONBOARDING_REVIEW: 'hold_onboarding_review',
  REQUEST_ADDITIONAL_DOCS: 'request_additional_docs',
  INTERNAL_REVIEW_ESCALATION: 'internal_review_escalation',
} as const;

export type AciEnforcementActionTypeV1 =
  (typeof ACI_ENFORCEMENT_ACTION_TYPE_V1)[keyof typeof ACI_ENFORCEMENT_ACTION_TYPE_V1];

export const ACI_ENFORCEMENT_REASON_CODE_V1 = {
  LINKAGE_INDICATOR_REVIEW: 'linkage_indicator_review',
  COMPLIANCE_QUEUE: 'compliance_queue',
  COUNSEL_DIRECTION: 'counsel_direction',
  MANUAL_SUPER_ADMIN: 'manual_super_admin',
  MISSING_CREDENTIAL_DOC: 'missing_credential_doc',
} as const;

export type AciEnforcementReasonCodeV1 =
  (typeof ACI_ENFORCEMENT_REASON_CODE_V1)[keyof typeof ACI_ENFORCEMENT_REASON_CODE_V1];

/** Pairs allowed server-side; UI should prefer these combinations. */
export const ACI_ENFORCEMENT_ACTION_REASON_PAIRS_V1: ReadonlyArray<
  readonly [AciEnforcementActionTypeV1, AciEnforcementReasonCodeV1]
> = [
  [ACI_ENFORCEMENT_ACTION_TYPE_V1.HOLD_ONBOARDING_REVIEW, ACI_ENFORCEMENT_REASON_CODE_V1.LINKAGE_INDICATOR_REVIEW],
  [ACI_ENFORCEMENT_ACTION_TYPE_V1.HOLD_ONBOARDING_REVIEW, ACI_ENFORCEMENT_REASON_CODE_V1.COMPLIANCE_QUEUE],
  [ACI_ENFORCEMENT_ACTION_TYPE_V1.HOLD_ONBOARDING_REVIEW, ACI_ENFORCEMENT_REASON_CODE_V1.MANUAL_SUPER_ADMIN],
  [ACI_ENFORCEMENT_ACTION_TYPE_V1.REQUEST_ADDITIONAL_DOCS, ACI_ENFORCEMENT_REASON_CODE_V1.MISSING_CREDENTIAL_DOC],
  [ACI_ENFORCEMENT_ACTION_TYPE_V1.REQUEST_ADDITIONAL_DOCS, ACI_ENFORCEMENT_REASON_CODE_V1.COUNSEL_DIRECTION],
  [ACI_ENFORCEMENT_ACTION_TYPE_V1.REQUEST_ADDITIONAL_DOCS, ACI_ENFORCEMENT_REASON_CODE_V1.MANUAL_SUPER_ADMIN],
  [ACI_ENFORCEMENT_ACTION_TYPE_V1.INTERNAL_REVIEW_ESCALATION, ACI_ENFORCEMENT_REASON_CODE_V1.LINKAGE_INDICATOR_REVIEW],
  [ACI_ENFORCEMENT_ACTION_TYPE_V1.INTERNAL_REVIEW_ESCALATION, ACI_ENFORCEMENT_REASON_CODE_V1.MANUAL_SUPER_ADMIN],
] as const;

export const ACI_BEHAVIOR_EVENT_SOURCE = {
  ADMIN_API: 'admin_api',
} as const;

export const ACI_BEHAVIOR_EVENT_TYPE_V1 = {
  OPERATIONAL_NOTE: 'operational_note',
  AUTHORITY_POSTURE_BUCKET: 'authority_posture_bucket',
  INSURANCE_POSTURE_SHIFT: 'insurance_posture_shift',
} as const;

export type AciBehaviorEventTypeV1 =
  (typeof ACI_BEHAVIOR_EVENT_TYPE_V1)[keyof typeof ACI_BEHAVIOR_EVENT_TYPE_V1];

export const ACI_BEHAVIOR_AUTHORITY_POSTURE_BUCKET = {
  NOT_AUTHORIZED: 'not_authorized',
  AUTHORIZED: 'authorized',
  OUT_OF_SERVICE: 'out_of_service',
  UNKNOWN: 'unknown',
} as const;

export const ACI_BEHAVIOR_INSURANCE_POSTURE_DIRECTION = {
  ADDED: 'added',
  REMOVED: 'removed',
  UNKNOWN: 'unknown',
} as const;

export type AciBehaviorAuthorityPostureBucket =
  (typeof ACI_BEHAVIOR_AUTHORITY_POSTURE_BUCKET)[keyof typeof ACI_BEHAVIOR_AUTHORITY_POSTURE_BUCKET];

export type AciBehaviorInsurancePostureDirection =
  (typeof ACI_BEHAVIOR_INSURANCE_POSTURE_DIRECTION)[keyof typeof ACI_BEHAVIOR_INSURANCE_POSTURE_DIRECTION];

export const ACI_BEHAVIOR_EVENT_OPERATIONAL_NOTE_MAX = 800 as const;

/** Max UTF-8 length for optional analyst notes (mirror backend validation). */
export const ACI_LINKAGE_ANALYST_FEEDBACK_NOTES_MAX_LENGTH = 1500 as const;

export const ACI_LINKAGE_ANALYST_VERDICT = {
  TRUE_LINK: 'true_link',
  FALSE_POSITIVE: 'false_positive',
  INCONCLUSIVE: 'inconclusive',
} as const;

export type AciLinkageAnalystVerdict =
  (typeof ACI_LINKAGE_ANALYST_VERDICT)[keyof typeof ACI_LINKAGE_ANALYST_VERDICT];

export const ACI_LINKAGE_PREVIEW_MAX_ANCHOR_USERS = 50 as const;
export const ACI_LINKAGE_PREVIEW_MAX_EMAIL_QUERIES = 8 as const;
export const ACI_LINKAGE_PREVIEW_MAX_PHONE_QUERIES = 8 as const;
export const ACI_LINKAGE_PREVIEW_MAX_VIN_QUERIES = 6 as const;

/** Phase 4.1 — distinct identifier families counted toward confluence (mirror backend helpers). */
export const ACI_LINKAGE_SIGNAL_FAMILY = {
  EMAIL: 'email',
  PHONE: 'phone',
  MC: 'mc',
  DOT: 'dot',
  VIN: 'vin',
} as const;

export type AciLinkageSignalFamily =
  (typeof ACI_LINKAGE_SIGNAL_FAMILY)[keyof typeof ACI_LINKAGE_SIGNAL_FAMILY];

/** Mirror `backend/src/config/authorityContinuityIntelligence.ts` `ACI_CONFLUENCE_MIN_DISTINCT_FAMILIES`. */
export const ACI_CONFLUENCE_MIN_DISTINCT_FAMILIES = 2 as const;

/** Phase 3 regulatory snapshot JSON contract (mirror backend). */
export const ACI_REGULATORY_SNAPSHOT_SCHEMA_VERSION = '2026-05-02.1' as const;

export const ACI_FMCSA_SNAPSHOT_SOURCE = 'fmcsa_qc_services' as const;

/** Cache TTL (ms) — mirror backend `ACI_REGULATORY_SNAPSHOT_CACHE_TTL_MS`; UI may show “fresh for N hours”. */
export const ACI_REGULATORY_SNAPSHOT_CACHE_TTL_MS = 86_400_000;

/** Firestore `system/featureFlags` — same as client `featureFlagService` document. */
export const ACI_FEATURE_FLAGS_FIRESTORE = {
  collectionId: 'system',
  documentId: 'featureFlags',
} as const;

/** API contract version (increment when response shape or legal bundle changes). */
export const ACI_PHASE_0_SCHEMA_VERSION = '2026-05-01.4' as const;

/** Legal / UX copy bundle version (align with `documentation/authority-continuity-intelligence-phase1-operational.md`). */
export const ACI_LEGAL_COPY_VERSION = '2026-05-01.2' as const;

/** Log / monitoring event names — no PII in message strings. */
export const ACI_OBSERVABILITY = {
  EVENT_PHASE0_STATUS: 'aci_phase0_status',
  EVENT_TOOL_DISABLED_ENV: 'aci_tool_disabled_env',
} as const;

/** v1: only super_admin may call ACI admin APIs (see Phase 1 operational doc). */
export const ACI_ALLOWED_GATE_ROLES_V1 = ['super_admin'] as const;

/** Audit `resource` values — stable identifiers for `auditLogs`. */
export const ACI_AUDIT_RESOURCE = {
  PHASE0_TOOL: 'aci/phase0-tool',
  REGULATORY_SNAPSHOT: 'aci/regulatory-snapshot',
  LINKAGE_PREVIEW: 'aci/linkage-preview',
  LINKAGE_ANALYST_FEEDBACK: 'aci/linkage-analyst-feedback',
  LINKAGE_ANALYST_FEEDBACK_LIST: 'aci/linkage-analyst-feedback-list',
  LINKAGE_PREVIEW_RUN_LIST: 'aci/linkage-preview-run-list',
  BEHAVIOR_EVENTS: 'aci/behavior-events',
  CASES: 'aci/cases',
  CASE_ACTIONS: 'aci/case-actions',
  REGULATORY_SHARE_INTENTS: 'aci/regulatory-share-intents',
  EXPORT_PACKET: 'aci/export-packet',
} as const;

/** Relative to {@link ACI_ADMIN_API.CASES_PATH}: append `/${caseId}/actions` for Phase 6.2. */
export const ACI_ADMIN_CASE_ACTIONS_SUFFIX = '/actions' as const;

/** Audit `action` values — must stay stable for SIEM mapping. */
export const ACI_AUDIT_ACTION = {
  PHASE0_STATUS_VIEW: 'aci_phase0_status_view',
  PHASE0_STATUS_DENIED_ENV: 'aci_phase0_status_denied_env',
  PHASE0_STATUS_DENIED_FLAG: 'aci_phase0_status_denied_flag',
  REGULATORY_SNAPSHOT_CACHE_HIT: 'aci_regulatory_snapshot_cache_hit',
  REGULATORY_SNAPSHOT_FMCSA_SUCCESS: 'aci_regulatory_snapshot_fmcsa_success',
  REGULATORY_SNAPSHOT_FMCSA_FAILED: 'aci_regulatory_snapshot_fmcsa_failed',
  LINKAGE_PREVIEW_RUN: 'aci_linkage_preview_run',
  LINKAGE_ANALYST_FEEDBACK_CREATE: 'aci_linkage_analyst_feedback_create',
  LINKAGE_ANALYST_FEEDBACK_LIST_VIEW: 'aci_linkage_analyst_feedback_list_view',
  LINKAGE_PREVIEW_RUN_LIST_VIEW: 'aci_linkage_preview_run_list_view',
  BEHAVIOR_EVENT_CREATE: 'aci_behavior_event_create',
  BEHAVIOR_EVENT_LIST_VIEW: 'aci_behavior_event_list_view',
  CASE_CREATE: 'aci_case_create',
  CASE_LIST_VIEW: 'aci_case_list_view',
  CASE_VIEW: 'aci_case_view',
  CASE_UPDATE: 'aci_case_update',
  CASE_EVIDENCE_ATTACH: 'aci_case_evidence_attach',
  CASE_ACTION_APPEND: 'aci_case_action_append',
  CASE_ACTION_LIST_VIEW: 'aci_case_action_list_view',
  REGULATORY_SHARE_INTENT_CREATE: 'aci_regulatory_share_intent_create',
  REGULATORY_SHARE_INTENT_LIST_VIEW: 'aci_regulatory_share_intent_list_view',
  REGULATORY_SHARE_INTENT_DENIED_ENV: 'aci_regulatory_share_intent_denied_env',
  EXPORT_PACKET_MANIFEST_VIEW: 'aci_export_packet_manifest_view',
  EXPORT_PACKET_ASSEMBLE: 'aci_export_packet_assemble',
} as const;

/** API error `code` values for client handling. */
export const ACI_API_ERROR_CODE = {
  LINKAGE_ENV_DISABLED: 'ACI_LINKAGE_ENV_DISABLED',
  FEATURE_FLAG_DISABLED: 'ACI_FEATURE_FLAG_DISABLED',
  FEATURE_FLAGS_READ_FAILED: 'ACI_FEATURE_FLAGS_READ_FAILED',
  COMPANY_NOT_FOUND: 'ACI_COMPANY_NOT_FOUND',
  COMPANY_NO_MC_DOT: 'ACI_COMPANY_NO_MC_DOT',
  FMCSA_QUOTA_EXCEEDED: 'ACI_FMCSA_QUOTA_EXCEEDED',
  LINKAGE_PREVIEW_QUOTA_EXCEEDED: 'ACI_LINKAGE_PREVIEW_QUOTA_EXCEEDED',
  LINKAGE_FEEDBACK_VERDICT_INVALID: 'ACI_LINKAGE_FEEDBACK_VERDICT_INVALID',
  LINKAGE_FEEDBACK_NOTES_TOO_LONG: 'ACI_LINKAGE_FEEDBACK_NOTES_TOO_LONG',
  LINKAGE_FEEDBACK_QUOTA_EXCEEDED: 'ACI_LINKAGE_FEEDBACK_QUOTA_EXCEEDED',
  LINKAGE_FEEDBACK_LIST_QUOTA_EXCEEDED: 'ACI_LINKAGE_FEEDBACK_LIST_QUOTA_EXCEEDED',
  LINKAGE_PREVIEW_RUN_LIST_QUOTA_EXCEEDED: 'ACI_LINKAGE_PREVIEW_RUN_LIST_QUOTA_EXCEEDED',
  BEHAVIOR_EVENT_INVALID: 'ACI_BEHAVIOR_EVENT_INVALID',
  BEHAVIOR_EVENT_QUOTA_EXCEEDED: 'ACI_BEHAVIOR_EVENT_QUOTA_EXCEEDED',
  BEHAVIOR_EVENT_LIST_QUOTA_EXCEEDED: 'ACI_BEHAVIOR_EVENT_LIST_QUOTA_EXCEEDED',
  CASE_INVALID: 'ACI_CASE_INVALID',
  CASE_NOT_FOUND: 'ACI_CASE_NOT_FOUND',
  CASE_CREATE_QUOTA_EXCEEDED: 'ACI_CASE_CREATE_QUOTA_EXCEEDED',
  CASE_LIST_QUOTA_EXCEEDED: 'ACI_CASE_LIST_QUOTA_EXCEEDED',
  CASE_MUTATE_QUOTA_EXCEEDED: 'ACI_CASE_MUTATE_QUOTA_EXCEEDED',
  CASE_EVIDENCE_LIMIT: 'ACI_CASE_EVIDENCE_LIMIT',
  CASE_EVIDENCE_DUPLICATE: 'ACI_CASE_EVIDENCE_DUPLICATE',
  CASE_PREVIEW_RUN_NOT_FOUND: 'ACI_CASE_PREVIEW_RUN_NOT_FOUND',
  CASE_PREVIEW_RUN_COMPANY_MISMATCH: 'ACI_CASE_PREVIEW_RUN_COMPANY_MISMATCH',
  CASE_ACTION_INVALID: 'ACI_CASE_ACTION_INVALID',
  CASE_ACTION_QUOTA_EXCEEDED: 'ACI_CASE_ACTION_QUOTA_EXCEEDED',
  CASE_ACTION_LIST_QUOTA_EXCEEDED: 'ACI_CASE_ACTION_LIST_QUOTA_EXCEEDED',
  REGULATORY_SHARE_ENV_DISABLED: 'ACI_REGULATORY_SHARE_ENV_DISABLED',
  REGULATORY_SHARE_INVALID: 'ACI_REGULATORY_SHARE_INVALID',
  REGULATORY_SHARE_QUOTA_EXCEEDED: 'ACI_REGULATORY_SHARE_QUOTA_EXCEEDED',
  REGULATORY_SHARE_LIST_QUOTA_EXCEEDED: 'ACI_REGULATORY_SHARE_LIST_QUOTA_EXCEEDED',
  EXPORT_PACKET_INVALID: 'ACI_EXPORT_PACKET_INVALID',
  EXPORT_PACKET_QUOTA_EXCEEDED: 'ACI_EXPORT_PACKET_QUOTA_EXCEEDED',
  EXPORT_PACKET_RUN_NOT_FOUND: 'ACI_EXPORT_PACKET_RUN_NOT_FOUND',
  EXPORT_PACKET_RUN_COMPANY_MISMATCH: 'ACI_EXPORT_PACKET_RUN_COMPANY_MISMATCH',
} as const;

/** Default retention targets (days); counsel may override in policy. */
export const ACI_RETENTION_RAW_CROSSWALK_DAYS = 90 as const;
export const ACI_RETENTION_CASE_DECISION_DAYS = 2555 as const;

/**
 * Default regulatory-export category allow-list (Phase 6).
 * Counsel may extend; do not add high-sensitivity categories without review.
 */
export const ACI_REGULATORY_EXPORT_ALLOWLIST_DEFAULT = [
  'aggregated_linkage_summary',
  'mc_dot_identifier_list',
  'fmcsa_snapshot_reference',
  'timestamps_and_schema_versions',
] as const;

/** Super-admin UI: short label for each allow-list key (API request values stay the snake_case keys). */
export const ACI_REGULATORY_EXPORT_CATEGORY_UI_LABEL_V1: Record<
  (typeof ACI_REGULATORY_EXPORT_ALLOWLIST_DEFAULT)[number],
  string
> = {
  aggregated_linkage_summary: 'Linkage summary (high-level, aggregated)',
  mc_dot_identifier_list: 'Motor carrier numbers (MC / DOT)',
  fmcsa_snapshot_reference: 'FMCSA public snapshot reference',
  timestamps_and_schema_versions: 'Timestamps and version metadata',
};

export function aciRegulatoryExportCategoryUiLabel(cat: string): string {
  const map = ACI_REGULATORY_EXPORT_CATEGORY_UI_LABEL_V1 as Record<string, string>;
  return map[cat] ?? cat;
}

/** Plain-language distribution notice for the SPA (JSON payloads still embed {@link ACI_EXPORT_WATERMARK_V1}). */
export const ACI_EXPORT_WATERMARK_UI_DISTRIBUTION_NOTICE =
  'Super-admin use only. Do not share with brokers, carriers, regulators, or the public unless counsel has approved the process. Content is limited to aggregated facts and references allowed under internal policy.';

/** True when `cat` is in {@link ACI_REGULATORY_EXPORT_ALLOWLIST_DEFAULT} (mirror backend). */
export function isAllowedAciRegulatoryExportCategory(cat: string): boolean {
  return (ACI_REGULATORY_EXPORT_ALLOWLIST_DEFAULT as readonly string[]).includes(cat);
}

/** Parse `exportCategories` for Phase 6.3–6.4 (mirror backend). */
export function parseAciRegulatoryExportCategoryArray(raw: unknown): string[] | null {
  if (!Array.isArray(raw)) return null;
  const out: string[] = [];
  for (const x of raw) {
    if (typeof x !== 'string') return null;
    const s = x.trim();
    if (!isAllowedAciRegulatoryExportCategory(s)) return null;
    if (!out.includes(s)) out.push(s);
  }
  out.sort();
  if (out.length < 1) return null;
  return out;
}

/** Internal contact for ACI / linkage indicator disputes (super-admin tooling). */
export const ACI_DISPUTE_CONTACT_EMAIL = 'founder@tranzit.io' as const;

/**
 * Single source for SPA disclaimers and API `legal` payloads (Phase 1).
 * Wording is risk-based, not accusatory.
 */
export const ACI_LEGAL_COPY = {
  productOneLine: `${AUTHORITY_CONTINUITY_INTELLIGENCE_DISPLAY_NAME} surfaces potential cross-entity identifier overlap for internal review only.`,
  limitation:
    'Outputs are heuristic risk indicators, not legal findings. False positives are possible. Do not treat any score or flag as proof of wrongdoing.',
  notAccusation:
    'Do not describe carriers or individuals as “illegal,” “fraudulent,” or “criminal” based solely on ACI output.',
  disputeIntro: 'Entities may dispute indicators through your published internal process. Contact:',
} as const;
